import { useEffect } from 'react';

import { useAppDispatch } from '@/app/store/hooks';
import { baseApi } from '@/shared/api/baseApi';
import { connectSocket, disconnectSocket, OUTSTANDING_ITEMS_EVENT } from '@/shared/api/socket';
import { getUserId } from '@/shared/lib/tokenStorage';

/**
 * Keeps the outstanding list live while the director has it open.
 *
 * The legacy portal took the socket's payload and pushed it straight into a
 * Redux array (`setDocuments([data])`), which meant the screen's contents came
 * from two sources that could disagree — and a malformed push corrupted the
 * list until a reload. Here the event only *invalidates* the `Document` tag:
 * RTK Query refetches from the API, so the server stays the single source of
 * truth and the payload shape is never trusted.
 *
 * Mounted once, in the app shell. Without a configured server the connection is
 * simply never made and the app behaves as it did before.
 */
export const useOutstandingSocket = (): void => {
  const dispatch = useAppDispatch();

  useEffect(() => {
    const memberId = getUserId() ?? '';
    const socket = connectSocket(memberId);
    if (socket === null) return;

    const refresh = () => {
      dispatch(baseApi.util.invalidateTags(['Document']));
    };

    socket.on(OUTSTANDING_ITEMS_EVENT, refresh);

    // One effect, so the connect and the release are always paired. Splitting
    // the teardown into its own `[]` effect meant a re-run of this one acquired
    // a hold that was never released.
    return () => {
      socket.off(OUTSTANDING_ITEMS_EVENT, refresh);
      disconnectSocket();
    };
  }, [dispatch]);
};
